import { Box, Button, Container, Tab, Tabs, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuthInfo } from "src/allauth/auth/hooks";
import ExpectationsSection from "../../components/role-faqs/ExpectationsSection";
import FAQSection from "../../components/role-faqs/FAQSection";
import OutcomesSection from "../../components/role-faqs/OutcomesSection";
import RoleFaqsHero from "../../components/role-faqs/RoleFaqsHero";
import TestimonialsSection from "../../components/role-faqs/TestimonialsSection";
import { ROLE_FAQS_CONTENT, type RoleFaqsKey } from "../../content/roleFaqsContent";

const ROLES: RoleFaqsKey[] = ["athlete", "guardian", "coach", "organization"];

/** FAQ role slugs → intake survey lanes (guardian uses parent intake). */
const INTAKE_PATH_BY_ROLE: Record<RoleFaqsKey, string> = {
  athlete: "/intake/athlete",
  guardian: "/intake/parent",
  coach: "/intake/coach",
  organization: "/intake/organization",
};

interface RoleFaqsPageProps {
  role: RoleFaqsKey;
}

const RoleFaqsPage: React.FC<RoleFaqsPageProps> = ({ role }) => {
  const content = ROLE_FAQS_CONTENT[role];
  const hasTestimonials = !!(content.testimonials?.length);
  const { user } = useAuthInfo();

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const roleSwitcher = useMemo(
    () => (
      <Tabs
        value={role}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          "& .MuiTab-root": { color: "rgba(255,255,255,0.85)", minHeight: 40 },
          "& .Mui-selected": { color: "secondary.main", fontWeight: 600 },
          "& .MuiTabs-indicator": { bgcolor: "secondary.main" },
        }}
      >
        {ROLES.map((r) => (
          <Tab
            key={r}
            label={content.role === r ? content.title : ROLE_FAQS_CONTENT[r].title}
            component={RouterLink}
            to={`/faqs/${r}`}
            value={r}
          />
        ))}
      </Tabs>
    ),
    [role, content]
  );

  function getStartedButton() {
    return role === 'athlete' || role === 'guardian' ? (
      <Box>
        <Button
          component={RouterLink}
          to={INTAKE_PATH_BY_ROLE[role]}
          variant="contained"
          color="secondary"
          sx={{ textTransform: "none", fontWeight: 600, px: 4, mr: 1 }}
          data-attr="role-faqs-page_cta-get-started"
        >
          Inquire For More
        </Button>
        {!user &&
          <Button
            component={RouterLink}
            to={"/account/signup?next=/account/subscriptions"}
            variant="contained"
            color="primary"
            sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
            data-attr="role-faqs-page_cta-signup"
          >
            Sign Up
          </Button>
        }
      </Box>
    ) : <Button
      component={RouterLink}
      to={INTAKE_PATH_BY_ROLE[role]}
      variant="contained"
      sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
      data-attr="role-faqs-page_cta-get-started"
    >
      Get Started
    </Button>
  }

  const anchorStrip = useMemo(
    () => (
      <>
        {getStartedButton()}


        <Typography component="span" sx={{ color: "inherit", opacity: 0.8, alignContent: "space-around" }}>
          |
        </Typography>
        <Button component="a" href="#faqs" onClick={(e) => scrollToSection(e, "faqs")} sx={{ color: "inherit", textTransform: "none" }} data-attr="role-faqs-page_link-faqs">
          FAQs
        </Button>
        <Typography component="span" sx={{ color: "inherit", opacity: 0.8, alignContent: "space-around" }}>
          |
        </Typography>
        <Button component="a" href="#expectations" onClick={(e) => scrollToSection(e, "expectations")} sx={{ color: "inherit", textTransform: "none" }} data-attr="role-faqs-page_link-expectations">
          Expectations
        </Button>
        <Typography component="span" sx={{ color: "inherit", opacity: 0.8, alignContent: "space-around" }}>
          |
        </Typography>
        <Button component="a" href="#outcomes" onClick={(e) => scrollToSection(e, "outcomes")} sx={{ color: "inherit", textTransform: "none" }} data-attr="role-faqs-page_link-outcomes">
          Outcomes
        </Button>
        {hasTestimonials && (
          <>
            <Typography component="span" sx={{ color: "inherit", opacity: 0.8, alignContent: "space-around" }}>
              |
            </Typography>
            <Button component="a" href="#testimonials" onClick={(e) => scrollToSection(e, "testimonials")} sx={{ color: "inherit", textTransform: "none" }} data-attr="role-faqs-page_link-testimonials">
              Testimonials
            </Button>
          </>
        )}
      </>
    ),
    [hasTestimonials, role]
  );

  return (
    <Box>
      <RoleFaqsHero
        title={content.title}
        intro={content.intro}
        roleSwitcher={roleSwitcher}
        heroImageUrl={content.roleSelectImage || "/images/athlete.jpg"}
        anchorStrip={anchorStrip}
      />

      <Container maxWidth="md" sx={{ py: 6 }}>
        <ExpectationsSection
          heading={`${content.role.charAt(0).toUpperCase() + content.role.slice(1)} Expectations`}
          sectionTitle={content.expectations.sectionTitle}
          steps={content.expectations.steps}
          id="expectations"
        />
        <OutcomesSection outcomes={content.outcomes} id="outcomes" />
        {content.testimonials?.length ? (
          <TestimonialsSection testimonials={content.testimonials} id="testimonials" />
        ) : null}
        <FAQSection faqs={content.faqs} id="faqs" />
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          {role === 'athlete' || role === 'guardian' ? (
            <Box>
              <Button
                component={RouterLink}
                to={INTAKE_PATH_BY_ROLE[role]}
                variant="contained"
                size="large"
                color="primary"
                sx={{ textTransform: "none", fontWeight: 600, px: 4, mr: 2 }}
                data-attr="role-faqs-page_cta-get-started"
              >
                Inquire For More
              </Button>
              <Button
                component={RouterLink}
                to={"/account/signup?next=/account/subscriptions"}
                variant="contained"
                size="large"
                color="secondary"
                sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
                data-attr="role-faqs-page_cta-signup"
              >
                Sign Up
              </Button>
            </Box>
          ) : <Button
            component={RouterLink}
            to={INTAKE_PATH_BY_ROLE[role]}
            variant="contained"
            size="large"
            sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
            data-attr="role-faqs-page_cta-get-started"
          >
            Get Started
          </Button>}
        </Box>
      </Container>
    </Box>
  );
};

export default RoleFaqsPage;
